import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ZoomIn } from "lucide-react";

const getCroppedFile = (src, cropPixels) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const size = 512;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(
        img,
        cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height,
        0, 0, size, size
      );
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error("crop failed"));
        resolve(new File([blob], "photo.jpg", { type: "image/jpeg" }));
      }, "image/jpeg", 0.9);
    };
    img.onerror = reject;
    img.src = src;
  });

export const ImageCropDialog = ({ open, image, onCancel, onCropped }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState(null);
  const [working, setWorking] = useState(false);

  const onComplete = useCallback((_, px) => setAreaPixels(px), []);

  const reset = () => { setZoom(1); setCrop({ x: 0, y: 0 }); setAreaPixels(null); };

  const cancel = () => { reset(); onCancel(); };

  const apply = async () => {
    if (!areaPixels) return;
    setWorking(true);
    try {
      const file = await getCroppedFile(image, areaPixels);
      onCropped(file);
    } finally {
      setWorking(false);
      reset();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && cancel()}>
      <DialogContent className="bg-white max-w-md" data-testid="crop-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Position Your Photo</DialogTitle>
          <DialogDescription>Drag to reposition and zoom to frame the photo.</DialogDescription>
        </DialogHeader>
        <div className="relative w-full h-64 bg-[#1C241B] rounded-lg overflow-hidden">
          {image && (
            <Cropper
              image={image}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onComplete}
            />
          )}
        </div>
        <div className="flex items-center gap-3 py-2">
          <ZoomIn className="h-4 w-4 text-[#7A8A78]" />
          <Slider value={[zoom]} min={1} max={3} step={0.01} onValueChange={(v) => setZoom(v[0])} data-testid="crop-zoom-slider" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={cancel}>Cancel</Button>
          <Button onClick={apply} disabled={working || !areaPixels} data-testid="apply-crop-button" className="bg-[#2D5A27] hover:bg-[#23471F] text-white">
            {working ? "Applying..." : "Apply"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
